import { useEffect, useState } from "react";
import { Form as FinalForm } from "react-final-form";
import { useIntl } from "react-intl";
import { Message } from "primereact/message";
import { Tag } from "primereact/tag";
import Section from "../../common/section";
import TooltipButton from "../../common/tooltipButton";
import { errorMessageId } from "../../../helpers/ErrorMessage";
import SMTPForm, { SMTP_CREDENTIALS_REQUIRED, smtpRequest } from "./SMTPForm";
import SmtpTestEmailButton from "./SmtpTestEmailButton";
import "./smtp-tab.scss";

// The status the last Send test email gives the settings in use: none since
// they were saved (or ever) is "not_tested"
export const smtpStatus = (smtp) => {
  if (!smtp?.lastTest) return "not_tested";
  return smtp.lastTest.ok ? "working" : "not_working";
};

const STATUS_TAG = {
  working: "edexlms-tag-success",
  not_working: "edexlms-tag-danger",
  not_tested: "edexlms-tag-warning",
};

const STATUS_ICON = {
  working: "pi pi-check",
  not_working: "pi pi-times",
  not_tested: "pi pi-clock",
};

// "mail.acme.example:2525" as the server and port the failure messages name
const serverParts = (server) => {
  const at = String(server || "").lastIndexOf(":");
  return at > 0
    ? { server: server.slice(0, at), port: server.slice(at + 1) }
    : { server: server || "", port: "" };
};

// Only the settings themselves: the form starts again when they change (after a
// save or a reset), not when a test changes the status
const settingsKey = (smtp) =>
  JSON.stringify([
    smtp?.server,
    smtp?.port,
    smtp?.senderName,
    smtp?.senderEmail,
    smtp?.hasUserName,
    smtp?.hasPassword,
    smtp?.usesDefault,
  ]);

// Settings > SMTP, laid out like Company sign-in: a status header from the last
// test email (with Send test email and Reset to default), the settings in three
// steps beside the last test, and one Save bar. Like the other settings tabs it
// is read only until the page's Edit is pressed (editing); Cancel ends editing
// (onEditingDone), and so does a save (the page does that). The test email
// always uses the saved settings, so it waits while there are unsaved changes.
const SmtpTab = ({
  smtp,
  canUpdate,
  editing = false,
  onEditingDone = () => {},
  saving,
  apiErrorCode,
  onSave,
  onReset,
  onRefresh,
}) => {
  const intl = useIntl();
  const message = (id, values) => intl.formatMessage({ id }, values);
  const when = (value) =>
    intl.formatDate(value, {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });

  const key = settingsKey(smtp);
  // The API refused blank credentials: shown until both are entered, the
  // changes are discarded or the settings are saved
  const [refused, setRefused] = useState(false);
  useEffect(() => {
    if (apiErrorCode === SMTP_CREDENTIALS_REQUIRED) setRefused(true);
  }, [apiErrorCode]);
  useEffect(() => {
    setRefused(false);
  }, [key]);

  const usesDefault = smtp?.usesDefault === true;
  const status = smtpStatus(smtp);
  const lastTest = smtp?.lastTest;
  const lastSuccess = smtp?.lastSuccess;
  const scope = usesDefault ? "default" : "own";
  const failure =
    (lastTest &&
      !lastTest.ok &&
      message(
        errorMessageId({ response: lastTest.reason }, intl, "smtp_test_failed"),
        serverParts(lastTest.server),
      )) ||
    null;
  const saveErrorId =
    (apiErrorCode &&
      apiErrorCode !== SMTP_CREDENTIALS_REQUIRED &&
      errorMessageId(
        { response: apiErrorCode },
        intl,
        "something_else_wrong",
      )) ||
    null;
  const noPermission = canUpdate ? "" : message("no_permission");
  const tooltipOptions = { showOnDisabled: true, position: "bottom" };

  return (
    <FinalForm
      key={key}
      initialValues={{ ...smtp, userName: "", password: "" }}
      onSubmit={(values) => onSave(smtpRequest(values))}
      render={({ handleSubmit, form, dirty }) => (
        <form
          onSubmit={handleSubmit}
          noValidate
          className="smtp-tab flex flex-column gap-4"
        >
          <div
            className="edexlms-section smtp-status"
            data-testid="smtp-status-card"
          >
            <div className="smtp-status-text">
              <h2 className="smtp-status-title">{message("smtp_settings")}</h2>
              <p className="smtp-status-help">
                {message(`smtp_status_help_${status}_${scope}`)}
              </p>
              <div className="flex flex-wrap align-items-center gap-2 mt-2">
                <Tag
                  rounded
                  className={`smtp-status-tag ${STATUS_TAG[status]}`}
                  data-testid="smtp-status"
                >
                  <span className="flex align-items-center gap-2">
                    <i className={STATUS_ICON[status]} aria-hidden="true"></i>
                    {message(`smtp_status_${status}`)}
                  </span>
                </Tag>
                <span className="smtp-chip">
                  {message(`smtp_chip_${scope}`)}
                </span>
              </div>
              {(lastTest && (
                <p className="smtp-last-line" data-testid="smtp-last-line">
                  {lastTest.ok
                    ? message("smtp_last_test_delivered_line", {
                        when: when(lastTest.at),
                        by: lastTest.by,
                        to: lastTest.to,
                      })
                    : message("smtp_last_test_failed_line", {
                        when: when(lastTest.at),
                        by: lastTest.by,
                      })}
                </p>
              )) ||
                null}
            </div>
            <div className="smtp-status-actions flex flex-wrap gap-2">
              {/* Only once the details say the organisation has its own settings */}
              {(smtp?.usesDefault === false && (
                <TooltipButton
                  type="button"
                  outlined
                  severity="secondary"
                  icon="pi pi-undo"
                  label={message("reset_smtp")}
                  onClick={onReset}
                  disabled={!canUpdate || saving}
                  tooltip={noPermission}
                  tooltipOptions={tooltipOptions}
                />
              )) ||
                null}
              <SmtpTestEmailButton
                canUpdate={canUpdate}
                waitReason={(dirty && message("smtp_test_save_first")) || ""}
                onDone={onRefresh}
              />
            </div>
          </div>

          {(failure && (
            <Message
              className="justify-content-start"
              severity="error"
              text={failure}
              data-testid="smtp-failure"
            />
          )) ||
            null}

          <div className="smtp-layout">
            <SMTPForm
              disabled={!canUpdate || !editing}
              savedServer={smtp?.server}
              // An API without the per-field flags only sends hasCredentials
              hasUserName={Boolean(smtp?.hasUserName ?? smtp?.hasCredentials)}
              hasPassword={Boolean(smtp?.hasPassword ?? smtp?.hasCredentials)}
              refused={refused}
              usesDefault={usesDefault}
            />

            <div className="flex flex-column gap-4">
              <div data-testid="smtp-last-test">
                <Section title="smtp_last_test_title" className="smtp-side">
                  {(lastTest && (
                    <>
                      <dl className="smtp-facts">
                        <dt>{message("smtp_last_test_result")}</dt>
                        <dd>
                          <Tag
                            rounded
                            className={
                              lastTest.ok
                                ? "edexlms-tag-success"
                                : "edexlms-tag-danger"
                            }
                            value={message(
                              lastTest.ok
                                ? "smtp_last_test_delivered"
                                : "smtp_last_test_failed",
                            )}
                          />
                        </dd>
                        {(!lastTest.ok && (
                          <>
                            <dt>{message("smtp_last_test_reason")}</dt>
                            <dd>
                              {message(
                                `${errorMessageId({ response: lastTest.reason }, intl, "smtp_test_failed")}_short`,
                              )}
                            </dd>
                          </>
                        )) ||
                          null}
                        <dt>{message("smtp_last_test_when")}</dt>
                        <dd>{when(lastTest.at)}</dd>
                        <dt>{message("smtp_last_test_by")}</dt>
                        <dd>{lastTest.by}</dd>
                        {(lastTest.ok && (
                          <>
                            <dt>{message("smtp_last_test_to")}</dt>
                            <dd>{lastTest.to}</dd>
                          </>
                        )) ||
                          null}
                        <dt>{message("smtp_last_test_server")}</dt>
                        <dd className={lastTest.server ? "smtp-mono" : ""}>
                          {lastTest.server || message("smtp_chip_default")}
                        </dd>
                      </dl>
                      {(lastTest.ok && (
                        <p className="smtp-section-help m-0">
                          {message("smtp_last_test_change_hint")}
                        </p>
                      )) ||
                        null}
                    </>
                  )) || (
                    <p className="smtp-section-help m-0">
                      {message("smtp_last_test_none")}
                    </p>
                  )}
                </Section>
              </div>

              {(lastTest && !lastTest.ok && lastSuccess && (
                <div data-testid="smtp-last-success">
                  <Section
                    title="smtp_last_success_title"
                    className="smtp-side"
                  >
                    <dl className="smtp-facts">
                      <dt>{message("smtp_last_test_when")}</dt>
                      <dd>{when(lastSuccess.at)}</dd>
                      <dt>{message("smtp_last_test_by")}</dt>
                      <dd>{lastSuccess.by}</dd>
                    </dl>
                  </Section>
                </div>
              )) ||
                null}
            </div>
          </div>

          {(canUpdate && editing && (
            <div
              className={`smtp-action-bar ${dirty ? "is-dirty" : ""}`}
              data-testid="smtp-action-bar"
            >
              {(saveErrorId && (
                <Message
                  className="w-full justify-content-start"
                  severity="error"
                  text={message(saveErrorId)}
                />
              )) ||
                null}
              <div className="smtp-action-bar-row">
                <span className="smtp-action-bar-note" aria-live="polite">
                  {message(dirty ? "smtp_unsaved_changes" : "smtp_no_changes")}
                </span>
                <div className="flex flex-wrap gap-2">
                  <TooltipButton
                    type="button"
                    outlined
                    severity="secondary"
                    icon="pi pi-undo"
                    label={message("cancel")}
                    onClick={() => {
                      form.reset();
                      setRefused(false);
                      onEditingDone();
                    }}
                    disabled={saving}
                  />
                  <TooltipButton
                    type="submit"
                    icon="pi pi-check"
                    label={message("smtp_save")}
                    loading={saving}
                    disabled={!dirty || saving}
                  />
                </div>
              </div>
            </div>
          )) ||
            null}
        </form>
      )}
    />
  );
};

export default SmtpTab;
